import React, { useCallback, useEffect, useState } from 'react';

import ErrorState from '../../../commodity/components/ui/ErrorState';
import LoadingState from '../../../commodity/components/ui/LoadingState';
import { PendingReview, getPendingReviews, submitReviews } from '../api/reviews';

const SUBJECT_LABEL: Record<PendingReview['subject'], string> = {
  TEACHER: 'el profesor',
  COURSE: 'el curso',
};

/**
 * Classes the family has taken and not yet rated (TCH-12-FR-05).
 *
 * The list is derived server-side from delivered sessions minus reviews
 * already written, so it cannot drift from either. It renders nothing at all
 * when there is nothing owed — a dashboard panel saying "no tienes reseñas
 * pendientes" is noise on the screen a parent checks daily.
 */
const PendingReviews: React.FC = () => {
  const [pending, setPending] = useState<PendingReview[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<string | null>(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { pending: items } = await getPendingReviews();
      setPending(items ?? []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const key = (item: PendingReview) => `${item.transactionId}#${item.subject}`;

  const send = async (item: PendingReview) => {
    setSaving(true);
    setError(null);
    try {
      await submitReviews({
        transactionId: item.transactionId,
        dimensions: [{ subject: item.subject, rating, comment: comment.trim() || undefined }],
      });
      setOpen(null);
      setComment('');
      setRating(5);
      // Refetched rather than removed locally: the server decides what is
      // still owed, and a local splice would disagree with it the moment
      // anything else changes.
      await refresh();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState what="tus reseñas pendientes" compact />;
  if (error && pending === null) {
    return (
      <ErrorState action="cargar tus reseñas pendientes" error={error} onRetry={refresh} compact />
    );
  }
  if (!pending || pending.length === 0) return null;

  return (
    <section aria-labelledby="pending-reviews" style={{ marginBottom: 24 }}>
      <h3 id="pending-reviews" style={{ marginBottom: 8 }}>
        Cuéntanos cómo fue ({pending.length})
      </h3>
      {/* A ternary, not `error && ...`: `error` is `unknown`, so the short-circuit
          form has type `unknown` rather than ReactNode and will not compile. */}
      {error ? <ErrorState action="enviar tu reseña" error={error} compact /> : null}

      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
        {pending.map((item) => (
          <li key={key(item)} style={{ border: '1px solid #d1d5da', borderRadius: 8, padding: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <span>Califica {SUBJECT_LABEL[item.subject]}</span>
              <button type="button" onClick={() => setOpen(open === key(item) ? null : key(item))}>
                {open === key(item) ? 'Cancelar' : 'Escribir reseña'}
              </button>
            </div>

            {open === key(item) && (
              <div style={{ marginTop: 12 }}>
                <label
                  htmlFor={`rating-${key(item)}`}
                  style={{ display: 'block', marginBottom: 4 }}
                >
                  Estrellas
                </label>
                <select
                  id={`rating-${key(item)}`}
                  value={rating}
                  onChange={(e) => setRating(Number(e.target.value))}
                >
                  {[5, 4, 3, 2, 1].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>

                <label
                  htmlFor={`comment-${key(item)}`}
                  style={{ display: 'block', margin: '8px 0 4px' }}
                >
                  Comentario (opcional)
                </label>
                <textarea
                  id={`comment-${key(item)}`}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={3}
                  style={{ width: '100%' }}
                />

                <button
                  type="button"
                  onClick={() => send(item)}
                  disabled={saving}
                  style={{ marginTop: 8 }}
                >
                  {saving ? 'Enviando…' : 'Enviar reseña'}
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};

export default PendingReviews;
